import { phaserEvents } from '../events/EventCenter'
import { getPhaserGameSync } from '../PhaserGame'

/** `Phaser.Scenes.SHUTDOWN` (a stopped scene); not imported so this stays free of Phaser at load. */
const SCENE_STATUS_SHUTDOWN = 8

/**
 * Reason for input being locked
 * This helps with managing overlapping UI states
 */
export enum InputLockReason {
    CHAT = 'chat',
    CHAT_FOCUSED = 'chat_focused', // New reason for when chat input is focused
    MENU = 'menu',
    MODAL = 'modal',
    PASSWORD_MODAL = 'password_modal',
    WHITEBOARD = 'whiteboard',
    COMPUTER = 'computer',
    MAP_EDITOR = 'map_editor',
    CUSTOM = 'custom',
    FEEDBACK_DRAWER = 'feedback_drawer',
    /** Right-click camera pan — movement paused while dragging. */
    CAMERA_DRAG = 'camera_drag',
    /** SC-375: the Gallery video layout hides the map; switch layouts to walk again. */
    GALLERY_VIEW = 'gallery_view',
}

/**
 * InputController
 * Centralized service to manage game input locking/unlocking
 * This allows different UI components to request input locking
 * without conflicting with each other
 */
class InputController {
    private activeLocks: Set<InputLockReason> = new Set()
    private previousVelocityX: number = 0
    private previousVelocityY: number = 0

    /**
     * Lock input for a specific reason
     * Multiple UI components can lock input for different reasons
     */
    lockInput(reason: InputLockReason): void {
        // Only take action if this is a new lock
        if (!this.activeLocks.has(reason)) {
            //console.log(`[InputController] Locking input for reason: ${reason}`)
            this.activeLocks.add(reason)
            this.updateGameInput()

            // Emit event so other components can react
            phaserEvents.emit('input-locked', reason)
        }
    }

    /**
     * Unlock input for a specific reason
     * Will only re-enable game input if all locks are cleared
     */
    unlockInput(reason: InputLockReason): void {
        // Only take action if this lock exists
        if (this.activeLocks.has(reason)) {
            //console.log(`[InputController] Unlocking input for reason: ${reason}`)
            this.activeLocks.delete(reason)
            this.updateGameInput()

            // Emit event so other components can react
            phaserEvents.emit('input-unlocked', reason)
        }
    }

    /**
     * Check if input is currently locked for any reason
     */
    isInputLocked(): boolean {
        return this.activeLocks.size > 0
    }

    /**
     * Check if input is locked for a specific reason
     */
    isLockedFor(reason: InputLockReason): boolean {
        return this.activeLocks.has(reason)
    }

    /**
     * Check if chat is focused (input fields have focus)
     */
    isChatFocused(): boolean {
        return this.activeLocks.has(InputLockReason.CHAT_FOCUSED)
    }

    /**
     * Check if chat is open (but not necessarily focused)
     */
    isChatOpen(): boolean {
        return this.activeLocks.has(InputLockReason.CHAT)
    }

    /**
     * Get all current lock reasons
     */
    getActiveLocks(): InputLockReason[] {
        return Array.from(this.activeLocks)
    }

    /**
     * Clear all input locks
     * Use with caution - this overrides all current locks
     */
    clearAllLocks(): void {
        if (this.activeLocks.size > 0) {
            //console.log('[InputController] Clearing all input locks')
            const previousLocks = Array.from(this.activeLocks)
            this.activeLocks.clear()
            this.updateGameInput()

            // Emit events for each cleared lock
            previousLocks.forEach(reason => {
                phaserEvents.emit('input-unlocked', reason)
            })
        }
    }

    /** True when any active lock should block local player movement. */
    shouldDisableMovement(): boolean {
        return (
            this.activeLocks.has(InputLockReason.CHAT) ||
            this.activeLocks.has(InputLockReason.CHAT_FOCUSED) ||
            this.activeLocks.has(InputLockReason.MENU) ||
            this.activeLocks.has(InputLockReason.MODAL) ||
            this.activeLocks.has(InputLockReason.PASSWORD_MODAL) ||
            this.activeLocks.has(InputLockReason.WHITEBOARD) ||
            this.activeLocks.has(InputLockReason.COMPUTER) ||
            this.activeLocks.has(InputLockReason.MAP_EDITOR) ||
            this.activeLocks.has(InputLockReason.CUSTOM) ||
            this.activeLocks.has(InputLockReason.FEEDBACK_DRAWER) ||
            this.activeLocks.has(InputLockReason.CAMERA_DRAG) ||
            this.activeLocks.has(InputLockReason.GALLERY_VIEW)
        )
    }

    /**
     * Reconcile MyPlayer / keyboard state with lock set — fixes orphan isUIOpen without TAB.
     * Also detects stuck-velocity states where the physics body is moving but no keys are pressed.
     */
    syncMovementEnabled(): void {
        try {
            const gameScene = this.getGameScene()
            if (!gameScene) return

            if (this.shouldDisableMovement()) {
                if (gameScene.myPlayer && !gameScene.myPlayer.isInteractingWithUI()) {
                    this.disableGameInput(gameScene)
                }
            } else {
                const orphanUiOpen = gameScene.myPlayer?.isInteractingWithUI()
                const inputManagerOff = gameScene.inputManager && !gameScene.inputManager.isEnabled()
                const keyboardOff = gameScene.input?.keyboard && !gameScene.input.keyboard.enabled
                if (orphanUiOpen || inputManagerOff || keyboardOff) {
                    this.enableGameInput(gameScene)
                }

                // Stuck-velocity watchdog: if no locks are active and keyboard is enabled,
                // but the physics body still has velocity while no movement keys are pressed,
                // force a full key and velocity reset. This catches edge cases where the
                // disable/enable cycle left stale state.
                this.reconcileStuckVelocity(gameScene)
            }
        } catch (error) {
            console.error('[InputController] Error syncing movement:', error)
        }
    }

    /**
     * Update game input based on current lock state
     */
    private updateGameInput(): void {
        try {
            const gameScene = this.getGameScene()
            if (!gameScene) return

            if (this.shouldDisableMovement()) {
                this.disableGameInput(gameScene)
            } else {
                this.enableGameInput(gameScene)
            }
        } catch (error) {
            console.error('[InputController] Error updating game input:', error)
        }
    }

    /**
     * Disable game input
     */
    private disableGameInput(gameScene: any): void {
        // Tell player that UI is open
        if (gameScene.myPlayer) {
            gameScene.myPlayer.setUIOpen(true)

            // Store current velocity for later restoration
            if (gameScene.myPlayer.playerContainer?.body) {
                this.previousVelocityX = gameScene.myPlayer.playerContainer.body.velocity.x
                this.previousVelocityY = gameScene.myPlayer.playerContainer.body.velocity.y
            }

            // Stop player movement
            gameScene.myPlayer.setVelocity(0, 0)
            if (gameScene.myPlayer.playerContainer?.body) {
                gameScene.myPlayer.playerContainer.body.velocity.x = 0
                gameScene.myPlayer.playerContainer.body.velocity.y = 0
            }
        }

        // Disable scene keyboard input
        if (gameScene.input?.keyboard) {
            gameScene.input.keyboard.enabled = false
            gameScene.input.keyboard.resetKeys()
        }

        // Also use InputManager if available
        if (gameScene.inputManager) {
            gameScene.inputManager.disableKeys()
        }
    }

    /**
     * Enable game input.
     * Critically, resets all key states after re-enabling the keyboard to prevent
     * phantom stuck keys from the prior disable cycle. Without this, keys that
     * were held during the disable keep their stale isDown=false state and Phaser
     * won't transition them back without a fresh keydown event — causing the
     * player to appear stuck / rubberbanding.
     */
    private enableGameInput(gameScene: any): void {
        // Tell player that UI is closed
        if (gameScene.myPlayer) {
            gameScene.myPlayer.setUIOpen(false)
        }

        // Enable scene keyboard input and reset key states for a clean slate
        if (gameScene.input?.keyboard) {
            gameScene.input.keyboard.enabled = true
            gameScene.input.keyboard.resetKeys()
        }

        // Also use InputManager if available — resetAllKeys clears cursor/special key states
        if (gameScene.inputManager) {
            gameScene.inputManager.enableKeys()
            if (typeof gameScene.inputManager.resetAllKeys === 'function') {
                gameScene.inputManager.resetAllKeys()
            }
        }
    }

    /**
     * Detect and fix stuck velocity: physics body has non-zero velocity but all movement
     * keys read as not pressed and no locks are active. This self-heals the rare case
     * where the disable/enable cycle left orphan velocity.
     */
    private reconcileStuckVelocity(gameScene: any): void {
        const player = gameScene.myPlayer
        if (!player) return

        const body = player.playerContainer?.body
        if (!body) return

        const hasVelocity = body.velocity.x !== 0 || body.velocity.y !== 0
        if (!hasVelocity) return

        // Check if any movement key is actually pressed via Phaser key objects
        const keyboard = gameScene.input?.keyboard
        if (!keyboard || !keyboard.enabled) return

        const inputManager = gameScene.inputManager
        if (!inputManager) return

        // If inputManager reports enabled but no cursors are down, velocity is orphaned
        if (!inputManager.isEnabled()) return

        const cursors = inputManager.getCursors()
        const anyKeyDown =
            cursors.left?.isDown ||
            cursors.right?.isDown ||
            cursors.up?.isDown ||
            cursors.down?.isDown ||
            cursors.W?.isDown ||
            cursors.A?.isDown ||
            cursors.S?.isDown ||
            cursors.D?.isDown

        if (!anyKeyDown) {
            player.setVelocity(0, 0)
            body.velocity.x = 0
            body.velocity.y = 0
            keyboard.resetKeys()
        }
    }

    /**
     * Get current game scene
     */
    private getGameScene(): any {
        try {
            const phaserGame = getPhaserGameSync()
            if (!phaserGame) return null
            const scene = phaserGame.scene.keys.game
            // A stopped scene (a kicked or departed player) stays in `scene.keys` but its player and
            // physics bodies are destroyed: the lock from the feedback prompt that opens on leaving
            // then threw on `setVelocity` every time.
            const status = scene?.sys?.settings?.status
            if (typeof status === 'number' && status >= SCENE_STATUS_SHUTDOWN) return null
            return scene
        } catch (e) {
            return null
        }
    }
}

// Create singleton instance
const inputController = new InputController()
export default inputController 